import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const computerScienceUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Primitive Types & Using Objects",
  examWeight: "15–25%",
  focus: "Foundational programming concepts including variables, arithmetic, and the use of the String and Math classes.",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Introduction to Algorithms, Programming, and Compilers",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ The Building Blocks of Algorithms</h3>
            <p className="text-slate-700 leading-relaxed mb-4">An algorithm is a finite set of instructions to accomplish a task.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">1. Sequencing</h4>
                <p className="text-xs text-slate-600 mt-1">Executing instructions in a specific linear order.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. Selection</h4>
                <p className="text-xs text-slate-600 mt-1">Paths chosen via Boolean logic (if-statements).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">3. Iteration</h4>
                <p className="text-xs text-slate-600 mt-1">Repeating a process using loops.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 The Java Compilation Process</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <h4 className="font-bold text-blue-900">1. Source Code (.java)</h4>
                <p className="text-sm text-slate-700 mt-1">The human-readable code written in an IDE.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">2. Compiler</h4>
                <p className="text-sm text-slate-700 mt-1">Translates source code into bytecode and checks for syntax errors.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">3. Bytecode (.class) & JVM</h4>
                <p className="text-sm text-slate-700 mt-1">Bytecode is platform-independent. The Java Virtual Machine (JVM) executes it on specific hardware.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Types of Errors</h3>
            <div className="space-y-3">
              <KeyTerm term="Syntax Error" definition="Grammar rule violations (e.g., missing semicolon). Prevents compilation." />
              <KeyTerm term="Runtime Error" definition="Occurs during execution (e.g., division by zero). Causes the program to crash." />
              <KeyTerm term="Logic Error" definition="The program runs but produces wrong output due to incorrect algorithms." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Variables and Data Types",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📦 Variable Lifecycle</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Declaration</h4>
                <code className="text-xs">int score;</code>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Initialization</h4>
                <code className="text-xs">score = 10;</code>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💎 Java Primitive Types</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">int</h4>
                <p className="text-sm text-slate-700">32-bit whole numbers. Range: <InlineMath math="-2^{31}" /> to <InlineMath math="2^{31}-1" />.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">double</h4>
                <p className="text-sm text-slate-700">64-bit decimals. Can have round-off errors because decimal math is approximated in binary.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">boolean</h4>
                <p className="text-sm text-slate-700">Stores logical values: <code>true</code> or <code>false</code>.</p>
              </div>
            </div>
          </section>

          <ImportantNote>
            <strong>final Keywords:</strong> Constants are declared using <code>final</code>. Their values cannot be changed after initialization. Example: <code>final double PI = 3.14;</code>
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Expressions and Output",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">➗ Arithmetic and Division</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Critical: Integer Division</h4>
                <p className="text-sm text-slate-700 mt-1">When both operands are integers, the decimal is <strong>truncated</strong> (cut off), not rounded.</p>
                <div className="mt-2 bg-white p-2 rounded text-xs font-mono">5 / 2 = 2</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Double Division</h4>
                <p className="text-sm text-slate-700 mt-1">If at least one operand is a <code>double</code>, the result is a decimal.</p>
                <div className="mt-2 bg-white p-2 rounded text-xs font-mono">5.0 / 2 = 2.5</div>
              </div>
              <KeyTerm term="Modulus (%)" definition="Returns the remainder of a division. Example: 10 % 3 = 1." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💬 Output and Concatenation</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Print Methods</h4>
                <p className="text-sm text-slate-700"><code>System.out.print()</code> keeps the cursor on the same line, whereas <code>println()</code> moves it to the next.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Escape Sequences</h4>
                <div className="grid grid-cols-3 gap-2 mt-2 font-mono text-xs">
                  <div className="p-1 bg-white rounded border text-center">\" (Quote)</div>
                  <div className="p-1 bg-white rounded border text-center">\\ (Backslash)</div>
                  <div className="p-1 bg-white rounded border text-center">\n (Newline)</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Assignment Statements and Input",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⌨️ Reading Input</h3>
            <p className="text-slate-700 mb-4">The <code>Scanner</code> class is used to capture keyboard input from <code>System.in</code>.</p>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-indigo-900">Scanner Methods</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><code>nextInt()</code>: Reads the next integer.</li>
                <li><code>nextDouble()</code>: Reads the next double.</li>
                <li><code>nextLine()</code>: Reads the entire string until the next newline.</li>
              </ul>
            </div>
          </section>
          <ImportantNote>
            <strong>Assignment Flow:</strong> Evaluation happens <strong>Right-to-Left</strong>. The expression on the right is fully resolved before being stored in the variable on the left.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Casting and Range of Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎭 Casting Types</h3>
            <div className="space-y-3">
              <div className="p-4 bg-slate-50 border rounded-lg">
                <h4 className="font-bold text-slate-900">Narrowing Casting (Explicit)</h4>
                <p className="text-sm text-slate-700">Manually converting a larger type to a smaller type using <code>(type)</code>. Note: This truncates decimals.</p>
                <code className="block mt-2 text-xs">int i = (int) 4.95; // Result: 4</code>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-bold text-yellow-900">Rounding Technique</h4>
                <p className="text-sm text-slate-700 mb-2">Since casting truncates, use these formulas to round to the nearest whole number:</p>
                <div className="grid md:grid-cols-2 gap-2">
                  <div className="p-2 bg-white rounded border text-xs">Positive: <code>(int)(x + 0.5)</code></div>
                  <div className="p-2 bg-white rounded border text-xs">Negative: <code>(int)(x - 0.5)</code></div>
                </div>
              </div>
            </div>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📉 Integer Limits</h3>
            <p className="text-sm text-slate-700">Java integers are stored using 32 bits.</p>
            <KeyTerm term="Integer.MAX_VALUE" definition="2147483647 (If you add 1, it overflows to the minimum value)." />
            <KeyTerm term="Integer.MIN_VALUE" definition="-2147483648" />
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Compound Assignment Operators",
      content: (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Shorthand Operators</h3>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-700 mb-4">These perform an operation and re-assign the result in one step:</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-xs">
              <div className="p-2 bg-white border rounded">x += 1</div>
              <div className="p-2 bg-white border rounded">x -= 1</div>
              <div className="p-2 bg-white border rounded">x *= 2</div>
              <div className="p-2 bg-white border rounded">x /= 2</div>
              <div className="p-2 bg-white border rounded">x %= 2</div>
            </div>
          </div>
          <ImportantNote>
            <strong>Increment/Decrement:</strong> <code>x++</code> and <code>x--</code> are post-increment/decrement operators used to change a value by exactly 1.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Application Program Interface (API) and Libraries",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Library Structures</h3>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">java.lang</h4>
                <p className="text-sm text-slate-700 mt-1">Automatically imported. Contains <code>String</code>, <code>Math</code>, <code>System</code>, <code>Integer</code>, and <code>Double</code>.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                <h4 className="font-bold text-indigo-900">java.util</h4>
                <p className="text-sm text-slate-700 mt-1">Must be imported manually. Contains <code>Scanner</code> and <code>Random</code>.</p>
              </div>
            </div>
          </section>
          <KeyTerm term="API" definition="The documentation that describes how to use classes and methods within libraries." />
        </div>
      )
    },
    {
      id: "topic-1-8",
      title: "Topic 1.8: Documentation with Comments",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🖊️ Documentation Standards</h3>
            <div className="grid md:grid-cols-3 gap-2 mb-4 font-mono text-xs">
              <div className="p-2 bg-slate-100 rounded border">// Single Line</div>
              <div className="p-2 bg-slate-100 rounded border">/* Multi-Line */</div>
              <div className="p-2 bg-slate-100 rounded border">/** Javadoc */</div>
            </div>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Precondition</h4>
                <p className="text-sm text-slate-700">What must be true <em>before</em> a method runs (e.g., input must be positive).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Postcondition</h4>
                <p className="text-sm text-slate-700">What is guaranteed to be true <em>after</em> the method completes.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-9",
      title: "Topic 1.9: Method Signatures",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Anatomy of a Method</h3>
            <div className="bg-slate-800 text-slate-200 p-4 rounded-md font-mono text-sm mb-4">
              public static int add(int a, int b)
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 text-sm">
              <li><strong>Signature:</strong> The method name and the parameter types in order (e.g., <code>add(int, int)</code>).</li>
              <li><strong>Return Type:</strong> Specifies the data type of the result (e.g., <code>int</code>) or <code>void</code> if nothing is returned.</li>
              <li><strong>Overloading:</strong> Using the same method name but with different parameter lists.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-10",
      title: "Topic 1.10: Calling Class Methods",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ Static Methods</h3>
            <p className="text-sm text-slate-700 mb-4">Class (static) methods belong to the class rather than an object instance.</p>
            <KeyTerm term="Calling Syntax" definition="ClassName.methodName(arguments); (e.g., Math.abs(-5))" />
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 border rounded-lg">
                <h4 className="font-bold text-slate-900">Parameters</h4>
                <p className="text-xs text-slate-600 mt-1">Variables defined in the method header.</p>
              </div>
              <div className="p-4 bg-slate-50 border rounded-lg">
                <h4 className="font-bold text-slate-900">Arguments</h4>
                <p className="text-xs text-slate-600 mt-1">Actual values passed into the call.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-11",
      title: "Topic 1.11: Math Class",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧮 Static Math Methods</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Math.abs(x)</h4>
                <p className="text-xs text-slate-700 mt-1">Absolute value. Works for int/double.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Math.pow(b, e)</h4>
                <p className="text-xs text-slate-700 mt-1">Base raised to exponent. Returns <strong>double</strong>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Math.sqrt(x)</h4>
                <p className="text-xs text-slate-700 mt-1">Square root. Returns <strong>double</strong>.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Math.random()</h4>
                <p className="text-xs text-slate-700 mt-1">Returns <InlineMath math="0.0 \le r < 1.0" />.</p>
              </div>
            </div>
          </section>
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h4 className="font-bold text-amber-900">Random Integer Formula</h4>
            <p className="text-sm text-slate-700 mt-2">To get a random int in range <InlineMath math="[min, max]" />:</p>
            <code className="block mt-2 text-xs">(int) (Math.random() * (max - min + 1)) + min</code>
          </div>
        </div>
      )
    },
    {
      id: "topic-1-12",
      title: "Topic 1.12: Objects: Instances of Classes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Class vs. Object</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Class (Blueprint)</h4>
                <p className="text-xs text-slate-600">Defines attributes (state) and behaviors (methods).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Object (Instance)</h4>
                <p className="text-xs text-slate-600">A specific creation built from the class template.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-13",
      title: "Topic 1.13: Object Creation and Storage (Instantiation)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ The 'new' Keyword</h3>
            <p className="text-sm text-slate-700 mb-4">Allocates memory in the <strong>heap</strong> and calls a constructor.</p>
            <div className="space-y-3">
              <KeyTerm term="Constructor" definition="A special method with the same name as the class and NO return type. Initializes the object." />
              <KeyTerm term="null" definition="A reference variable that points to no memory address. Attempting to use it throws a NullPointerException." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-14",
      title: "Topic 1.14: Calling Instance Methods",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📞 Object Methods</h3>
            <p className="text-sm text-slate-700">Instance methods require an existing object to be called.</p>
            <div className="bg-slate-800 text-green-400 p-4 rounded-md font-mono text-xs mt-4">
              String s = new String("Hello");<br/>
              int len = s.length(); // length() is an instance method
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-15",
      title: "Topic 1.15: String Manipulation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧵 String Objects</h3>
            <KeyTerm term="Immutability" definition="Strings cannot be changed once created. Methods return NEW strings rather than modifying the original." />
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
              <h4 className="font-bold text-blue-900 mb-2">Essential Methods (AP Exam)</h4>
              <ul className="space-y-2 text-sm text-slate-700">
                <li><code>length()</code>: Returns character count.</li>
                <li><code>substring(from, to)</code>: Characters from <code>from</code> (inc) to <code>to</code> (exc).</li>
                <li><code>indexOf(str)</code>: First occurrence index; returns <code>-1</code> if not found.</li>
                <li><code>equals(other)</code>: Checks if content is identical (Use this instead of <code>==</code>).</li>
                <li><code>compareTo(other)</code>: Lexicographical comparison (dictionary order).</li>
              </ul>
            </div>
          </section>

          <ImportantNote>
            <strong>Indexing:</strong> Strings use 0-based indexing. The first character is at index 0. The length of a substring is always <code>to - from</code>.
          </ImportantNote>
        </div>
      )
    }
  ]
};

export default computerScienceUnit1Notes;